import type {
  ChannelThreadId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type {
  Message,
  MessageWithSupportTicketId,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { SupportTicketTimelineEventResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event-response";
import { SupportTicketFilterNames } from "@redotech/effect-api-shared/schema/support-ticket-filters";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import {
  EffectApiClientContext,
  type EffectApiContext,
} from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { useStreamConnected } from "@redotech/redo-merchant-app-common/stream/stream-provider";
import {
  ArrayStringFilterOperator,
  type ArrayStringFilterData,
} from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import { AdvancedFilterType } from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import {
  FilterOperator,
  type FilterGroup,
} from "@redotech/redo-model/views/views";
import {
  mergeSupportTicketDetailForCollection,
  shouldVerifyEmptyTranscript,
  SUPPORT_TICKET_LIST_REFETCH_INTERVAL_MS,
  useChannelThreadsData,
  useSingleSupportTicketDataCore,
  useSupportTicketData,
  useSupportTicketMessagesData,
  useSupportTicketPresenceData,
  useSupportTicketsData,
  useSupportTicketTimelineEventsData,
  useThreadMessagesData,
} from "@redotech/redo-support-v2/query/support-ticket-data-hooks";
import { supportTicketDetailQueryKey } from "@redotech/redo-support-v2/query/support-ticket-query-keys";
import type { SupportTicketThreadCollectionItem } from "@redotech/redo-support-v2/query/support-ticket-transcript-collections";
import type { SupportTicketPresenceRecord } from "@redotech/redo-support-v2/realtime/support-ticket-presence-collection";
import { createLiveQueryCollection, eq } from "@tanstack/react-db";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDebounce } from "usehooks-ts";
import { merchantQueryClient } from "../../react-query-client";
import {
  fetchSupportTicketMessages,
  fetchSupportTicketThreads,
  reconcileSupportTicketTranscript,
  type GlobalSupportTicketCollections,
} from "../db/collections";
import {
  DEFAULT_PAGE_SIZE,
  fetchSingleSupportTicket,
} from "../db/support-ticket-list-collection";
import { useQueryOptions } from "../filters/filters-context";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";
import { prefetchSupportRouteModule } from "../support-route-prefetch";

const SUPPORT_TICKET_PREFETCH_STALE_TIME_MS = 5 * 60 * 1000;
const SUPPORT_TICKET_INTENT_PREFETCH_DELAY_MS = 120;
const SUPPORT_TICKET_INTENT_PREFETCH_HOLD_MS = 30 * 1000;

interface PrefetchedSupportTicket {
  cleanupCallbacks: (() => Promise<void>)[];
  cleanupTimer: ReturnType<typeof setTimeout> | null;
  detailStarted: boolean;
  messagesStarted: boolean;
  threadsStarted: boolean;
}

export function useSupportTickets(
  page: number,
  pageSize = DEFAULT_PAGE_SIZE,
): {
  data: SupportTicketListItemResponse[];
  totalCount: number;
  isLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();
  const queryOptions = useQueryOptions();
  const streamConnected = useStreamConnected();

  return useSupportTicketsData({
    page,
    collections,
    filterGroup: queryOptions.filterGroup,
    sorts: queryOptions.sorts,
    pageSize,
    // Realtime SSE is the primary refresh path. Poll only as a fallback while
    // the stream is down, so new tickets still surface without a manual reload
    // when realtime delivery is unavailable. 0 disables polling.
    refetchIntervalMs: streamConnected
      ? 0
      : SUPPORT_TICKET_LIST_REFETCH_INTERVAL_MS,
  });
}

/**
 * First page of support tickets matching a free-text search across the whole
 * inbox (ticket number, subject, summary, customer name/email/phone) via the
 * server-side `words` filter. The term is debounced so typing does not fire a
 * request per keystroke. With an empty term it falls back to the active view's
 * filter so the list is never empty. Used by the merge picker.
 */
export function useSupportTicketSearch(searchTerm: string): {
  data: SupportTicketListItemResponse[];
  isLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();
  const queryOptions = useQueryOptions();
  const debouncedSearch = useDebounce(searchTerm.trim(), 300);

  const filterGroup = useMemo<FilterGroup>(() => {
    if (!debouncedSearch) {
      return queryOptions.filterGroup;
    }
    const wordsFilter: ArrayStringFilterData = {
      name: SupportTicketFilterNames.WORDS,
      type: AdvancedFilterType.ARRAY_TO_STRING,
      operator: ArrayStringFilterOperator.CONTAINS,
      value: [debouncedSearch],
    };
    return { operator: FilterOperator.AND, clauses: [wordsFilter] };
  }, [debouncedSearch, queryOptions.filterGroup]);

  const { data, isLoading } = useSupportTicketsData({
    page: 0,
    collections,
    filterGroup,
    sorts: queryOptions.sorts,
  });
  return { data, isLoading };
}

export function useSupportTicket(supportTicketId: SupportTicketId): {
  data: SupportTicketListItemResponse | undefined;
  isLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();
  const effectApi = useRequiredContext(EffectApiClientContext);

  return useSupportTicketData({
    supportTicketId,
    collections,
    fetchSupportTicket: (id) =>
      fetchSingleSupportTicket(effectApi, id, merchantQueryClient),
  });
}

export function useChannelThreads(supportTicketId: SupportTicketId): {
  data: SupportTicketThreadCollectionItem[];
  isLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();
  return useChannelThreadsData({ supportTicketId, collections });
}

export function useSupportTicketMessages(supportTicketId: SupportTicketId): {
  data: MessageWithSupportTicketId[];
  isLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();
  return useSupportTicketMessagesData({ supportTicketId, collections });
}

export function useThreadMessages(
  supportTicketId: SupportTicketId,
  threadIds: ChannelThreadId | readonly ChannelThreadId[],
): { data: Message[]; isLoading: boolean } {
  const collections = useGlobalSupportTicketCollections();
  return useThreadMessagesData({ supportTicketId, threadIds, collections });
}

export function useSupportTicketTimelineEvents(
  supportTicketId: SupportTicketId,
): { data: SupportTicketTimelineEventResponse[]; isLoading: boolean } {
  const collections = useGlobalSupportTicketCollections();
  return useSupportTicketTimelineEventsData({ supportTicketId, collections });
}

export function useSupportTicketPresence(supportTicketId: SupportTicketId): {
  data: SupportTicketPresenceRecord[];
} {
  const collections = useGlobalSupportTicketCollections();
  return useSupportTicketPresenceData({ supportTicketId, collections });
}

/**
 * Backend double-check for an apparently-empty transcript. When the transcript
 * has settled (not loading) with no threads or no messages — which the
 * on-demand collections can incorrectly produce after a view/filter change —
 * this re-fetches threads and messages directly and writes any results into the
 * collections so the live queries re-emit with data instead of showing the
 * empty state.
 *
 * Returns whether a verification fetch is in flight. The loader is keyed on the
 * row counts, so a genuinely empty transcript leaves the counts unchanged and
 * cannot loop, while a transcript that flips back to empty re-verifies.
 */
function useTranscriptEmptyStateFallback({
  supportTicketId,
  collections,
  effectApi,
  threadCount,
  messageCount,
  transcriptLoading,
}: {
  supportTicketId: SupportTicketId;
  collections: GlobalSupportTicketCollections;
  effectApi: EffectApiContext;
  threadCount: number;
  messageCount: number;
  transcriptLoading: boolean;
}): boolean {
  const { threads: threadsCollection, messages: messagesCollection } =
    collections;

  const { pending } = useLoad(
    async (signal) => {
      if (
        !shouldVerifyEmptyTranscript({
          transcriptLoading,
          threadCount,
          messageCount,
        })
      ) {
        return;
      }

      const [threads, messages] = await Promise.all([
        fetchSupportTicketThreads(effectApi, supportTicketId),
        fetchSupportTicketMessages(effectApi, supportTicketId),
      ]);
      if (signal.aborted) {
        return;
      }

      if (threads.length > 0 && threadsCollection.isReady()) {
        threadsCollection.utils.writeBatch(() => {
          for (const thread of threads) {
            threadsCollection.utils.writeUpsert(thread);
          }
        });
      }
      if (messages.length > 0 && messagesCollection.isReady()) {
        messagesCollection.utils.writeBatch(() => {
          for (const message of messages) {
            messagesCollection.utils.writeUpsert(message);
          }
        });
      }
    },
    [
      supportTicketId,
      transcriptLoading,
      threadCount,
      messageCount,
      threadsCollection,
      messagesCollection,
      effectApi,
    ],
  );

  return pending;
}

/**
 * Reconcile the opened ticket's transcript against the server on open.
 *
 * The thread/message collections are on-demand with a 5-minute staleTime, so a
 * merge that reshaped this ticket's thread set while its subset was still
 * "fresh" in cache stays hidden when you open it — the merged-in messages don't
 * appear until a manual reload. The SSE merge notification re-indexes live, but
 * it races with navigation and is not delivered to every tab/session, so it
 * cannot be the only path.
 *
 * This fetches the ticket's authoritative threads/messages on open and
 * reconciles them: rows already cached under the right ticket update in place
 * (already-cached messages stay on screen, merged-in messages slide in), while
 * a row mis-indexed under a now-merged source ticket is re-indexed. That avoids
 * the empty-then-repopulate flash a collection-level refetch produces. `pending`
 * lets the caller show a single clean loading bar while the first transcript
 * loads (nothing cached yet) instead of flashing between states.
 */
function useReconcileSupportTicketTranscriptOnOpen({
  supportTicketId,
  collections,
  effectApi,
}: {
  supportTicketId: SupportTicketId;
  collections: GlobalSupportTicketCollections;
  effectApi: EffectApiContext;
}): boolean {
  const { threads: threadsCollection, messages: messagesCollection } =
    collections;

  const { pending } = useLoad(
    async (signal) => {
      await reconcileSupportTicketTranscript({
        effectApi,
        threads: threadsCollection,
        messages: messagesCollection,
        supportTicketId,
        signal,
      });
    },
    [supportTicketId, threadsCollection, messagesCollection, effectApi],
  );

  return pending;
}

export function useSingleSupportTicketData(supportTicketId: SupportTicketId): {
  supportTicket: SupportTicketListItemResponse | undefined;
  threads: SupportTicketThreadCollectionItem[];
  messages: Message[];
  isLoading: boolean;
  supportTicketLoading: boolean;
  transcriptLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();
  const effectApi = useRequiredContext(EffectApiClientContext);

  const result = useSingleSupportTicketDataCore({
    supportTicketId,
    collections,
    fetchSupportTicket: (id) =>
      fetchSingleSupportTicket(effectApi, id, merchantQueryClient),
  });

  const verifyingEmptyTranscript = useTranscriptEmptyStateFallback({
    supportTicketId,
    collections,
    effectApi,
    threadCount: result.threads.length,
    messageCount: result.messages.length,
    transcriptLoading: result.transcriptLoading,
  });

  // Pull the authoritative transcript on open so a merge that reshaped this
  // ticket while its subset was cached fresh shows up without a manual reload.
  const reconcilingTranscript = useReconcileSupportTicketTranscriptOnOpen({
    supportTicketId,
    collections,
    effectApi,
  });

  // Only suppress the conversation-level empty state (no threads) behind the
  // loading bar while verifying or reconciling; an existing thread keeps
  // rendering and merged-in messages append once the reconcile write lands, so
  // a ticket that already has cached content never flashes back to a loading
  // state.
  const suppressEmptyState =
    (verifyingEmptyTranscript || reconcilingTranscript) &&
    result.threads.length === 0;

  return {
    ...result,
    isLoading: result.isLoading || suppressEmptyState,
    transcriptLoading: result.transcriptLoading || suppressEmptyState,
  };
}

export function usePrefetchSupportTicket(): {
  cancelPendingSupportTicketPrefetch: (
    supportTicketId?: SupportTicketId,
  ) => void;
  prefetchSupportTicket: (supportTicketId: SupportTicketId) => void;
} {
  const collections = useGlobalSupportTicketCollections();
  const effectApi = useRequiredContext(EffectApiClientContext);
  const pendingIntentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const pendingIntentIdRef = useRef<SupportTicketId | null>(null);
  const prefetchesRef = useRef(new Map<string, PrefetchedSupportTicket>());

  useEffect(() => {
    const prefetches = prefetchesRef.current;
    return () => {
      if (pendingIntentTimerRef.current) {
        clearTimeout(pendingIntentTimerRef.current);
      }
      for (const prefetch of prefetches.values()) {
        if (prefetch.cleanupTimer) {
          clearTimeout(prefetch.cleanupTimer);
        }
        for (const cleanup of prefetch.cleanupCallbacks) {
          void cleanup();
        }
      }
      prefetches.clear();
    };
  }, []);

  const prefetchNow = useCallback(
    (supportTicketId: SupportTicketId) => {
      const key = String(supportTicketId);
      let prefetch = prefetchesRef.current.get(key);
      if (!prefetch) {
        prefetch = {
          cleanupCallbacks: [],
          cleanupTimer: null,
          detailStarted: false,
          messagesStarted: false,
          threadsStarted: false,
        };
        prefetchesRef.current.set(key, prefetch);
      }

      if (prefetch.cleanupTimer) {
        clearTimeout(prefetch.cleanupTimer);
      }
      prefetch.cleanupTimer = setTimeout(() => {
        const currentPrefetch = prefetchesRef.current.get(key);
        if (!currentPrefetch) {
          return;
        }
        for (const cleanup of currentPrefetch.cleanupCallbacks) {
          void cleanup();
        }
        prefetchesRef.current.delete(key);
      }, SUPPORT_TICKET_INTENT_PREFETCH_HOLD_MS);

      void prefetchSupportRouteModule().catch(() => undefined);

      if (!prefetch.detailStarted) {
        prefetch.detailStarted = true;
        void merchantQueryClient
          .prefetchQuery({
            queryKey: supportTicketDetailQueryKey(
              supportTicketId,
              collections.supportTicketsList.queryScope,
            ),
            queryFn: async () => {
              const supportTicket = await fetchSingleSupportTicket(
                effectApi,
                supportTicketId,
                merchantQueryClient,
              );
              const existing =
                collections.supportTicketsList.collection.get(supportTicketId);
              collections.supportTicketsList.writeItem(
                mergeSupportTicketDetailForCollection(supportTicket, existing),
              );
              return supportTicket;
            },
            staleTime: SUPPORT_TICKET_PREFETCH_STALE_TIME_MS,
          })
          .catch(() => undefined);
      }

      if (!prefetch.threadsStarted) {
        prefetch.threadsStarted = true;
        const threadsQuery = createLiveQueryCollection((q) =>
          q
            .from({ thread: collections.threads })
            .where(({ thread }) => eq(thread.supportTicketId, supportTicketId))
            .orderBy(({ thread }) => thread.createdAt, "asc"),
        );
        prefetch.cleanupCallbacks.push(() => threadsQuery.cleanup());
        void threadsQuery.preload().catch(() => undefined);
      }

      if (!prefetch.messagesStarted) {
        prefetch.messagesStarted = true;
        const messagesQuery = createLiveQueryCollection((q) =>
          q
            .from({ message: collections.messages })
            .where(({ message }) =>
              eq(message.supportTicketId, supportTicketId),
            )
            .orderBy(({ message }) => message.sentAt, "asc"),
        );
        prefetch.cleanupCallbacks.push(() => messagesQuery.cleanup());
        void messagesQuery.preload().catch(() => undefined);
      }
    },
    [collections, effectApi],
  );

  const cancelPendingSupportTicketPrefetch = useCallback(
    (supportTicketId?: SupportTicketId) => {
      if (
        supportTicketId !== undefined &&
        pendingIntentIdRef.current !== supportTicketId
      ) {
        return;
      }
      if (pendingIntentTimerRef.current) {
        clearTimeout(pendingIntentTimerRef.current);
        pendingIntentTimerRef.current = null;
      }
      pendingIntentIdRef.current = null;
    },
    [],
  );

  const prefetchSupportTicket = useCallback(
    (supportTicketId: SupportTicketId) => {
      pendingIntentIdRef.current = supportTicketId;
      if (pendingIntentTimerRef.current) {
        clearTimeout(pendingIntentTimerRef.current);
      }
      pendingIntentTimerRef.current = setTimeout(() => {
        if (pendingIntentIdRef.current === supportTicketId) {
          prefetchNow(supportTicketId);
        }
      }, SUPPORT_TICKET_INTENT_PREFETCH_DELAY_MS);
    },
    [prefetchNow],
  );

  return useMemo(
    () => ({ cancelPendingSupportTicketPrefetch, prefetchSupportTicket }),
    [cancelPendingSupportTicketPrefetch, prefetchSupportTicket],
  );
}
